import {
  Component,
  computed,
  inject,
  output,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  UISVGIconComponent,
  UITypographyDirective,
  UIComponentSizeDirective,
  UILabelComponent
} from '@bannerflow/ui';
import { NotificationDto } from '@bannerflow/types.notification-service';
import { NotificationService } from '../../../services/notification.service';
import { NotificationClickEvent } from '../../../notification.types';
import { NotificationItemComponent } from '../notification-item/notification-item.component';

interface NotificationGroup {
  label: string;
  notifications: NotificationDto[];
}

@Component({
  imports: [
    NotificationItemComponent,
    UIComponentSizeDirective,
    UILabelComponent,
    UISVGIconComponent,
    UITypographyDirective
  ],
  selector: 'ns-notifications-list',
  templateUrl: './notifications-list.component.html',
  styleUrl: './notifications-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class NotificationsListComponent {
  private notificationService = inject(NotificationService);

  linkClicked = output();
  notificationClicked = output<NotificationClickEvent>();

  notifications = this.notificationService.notifications;
  groupedNotifications = computed(() => this.computeGroupedNotifications());

  protected handleLinkClicked(): void {
    this.linkClicked.emit();
  }

  protected handleNotificationClicked(event: NotificationClickEvent): void {
    this.notificationClicked.emit(event);
  }

  private computeGroupedNotifications(): NotificationGroup[] {
    const notifications = this.notifications();
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const todayNotifications = [];
    const yesterdayNotifications = [];
    const lastWeekNotifications = [];
    const olderNotifications = [];

    for (const notification of notifications) {
      const date = new Date(notification.created);
      date.setHours(0, 0, 0, 0);

      if (date.getTime() === now.getTime()) {
        todayNotifications.push(notification);
      } else if (date.getTime() === yesterday.getTime()) {
        yesterdayNotifications.push(notification);
      } else if (date > weekAgo) {
        lastWeekNotifications.push(notification);
      } else {
        olderNotifications.push(notification);
      }
    }

    const result: NotificationGroup[] = [];

    if (todayNotifications.length) {
      result.push({ label: 'Today', notifications: todayNotifications });
    }
    if (yesterdayNotifications.length) {
      result.push({
        label: 'Yesterday',
        notifications: yesterdayNotifications
      });
    }
    if (lastWeekNotifications.length) {
      result.push({
        label: 'Last 7 days',
        notifications: lastWeekNotifications
      });
    }
    if (olderNotifications.length) {
      result.push({ label: 'Older', notifications: olderNotifications });
    }

    return result;
  }
}
